"use client";

/**
 * Sync chat messages to/from Supabase so conversations follow the Google
 * account across devices — the same pattern as syncProfiles.ts, applied to
 * db.chatMessages instead of db.aiProfiles.
 *
 * Profiles synced already (see syncProfiles.ts), but chat history never
 * did: it lived only in this browser's IndexedDB. Signing in on a new
 * device brought your AI profiles back but every conversation was gone —
 * confirmed as the single biggest cross-device trust gap before real users
 * start relying on "signing in" meaning anything for their data.
 *
 * ChatMessage carries no ownerId of its own (unlike AiProfile) — the local
 * IndexedDB has always been implicitly single-user. Ownership for the
 * migrate-on-signin step is derived by cross-referencing each message's
 * profileId against aiProfiles already tagged with the signed-in user
 * (migrateLocalAnonymousProfilesToAuthUser runs first — see
 * lib/user/context.tsx) rather than needing a new field on ChatMessage.
 */

import { createClient } from "@/lib/supabase/client";
import { db } from "@/lib/db";
import { getCachedAuthUserId } from "@/lib/user/identity";
import type { ChatMessage } from "@/lib/db/schema";

const TABLE = "app_chat_messages";

// db.chatMessages.put() fires the same "creating" hook as add() for any row
// that doesn't already exist locally — which is every row hydrateMessages-
// FromSupabase pulls down on a fresh device. Without this guard, each one
// would immediately echo straight back to Supabase as a redundant upsert
// of data it was just read from. Harmless (idempotent, terminates — the
// hook doesn't fire again from inside the upsert itself), just wasted
// round-trips, worth skipping outright.
let hydrating = false;

type DbWithChatMessages = {
  chatMessages: {
    hook(event: "creating" | "deleting", handler: (...args: any[]) => void): void;
  };
};

export async function fetchMessagesForUser(userId: string): Promise<ChatMessage[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select("payload")
    .eq("user_id", userId);

  if (error) {
    console.warn("[syncMessages] fetch error:", error.message);
    return [];
  }

  const messages: ChatMessage[] = [];
  for (const row of data ?? []) {
    const payload = row?.payload;
    if (payload && typeof payload === "object" && payload.id) {
      messages.push(payload as ChatMessage);
    }
  }
  return messages;
}

export async function upsertMessageToSupabase(message: ChatMessage, userIdOverride?: string): Promise<void> {
  const userId = userIdOverride ?? getCachedAuthUserId();
  // Messages without a conversationId predate branching/multi-chat and
  // can't be reloaded via the conversationId-keyed query loadMessages()
  // uses anyway (see ChatInterface.tsx) — nothing useful to sync them for.
  if (!userId || !message.conversationId) return;

  const supabase = createClient();
  const { error } = await supabase
    .from(TABLE)
    .upsert(
      {
        id: message.id,
        user_id: userId,
        conversation_id: message.conversationId,
        payload: message,
        created_at: new Date(message.createdAt).toISOString(),
      },
      { onConflict: "id" }
    );

  if (error) {
    console.warn("[syncMessages] upsert error:", error.message);
  }
}

export async function deleteMessageFromSupabase(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) {
    console.warn("[syncMessages] delete error:", error.message);
  }
}

/**
 * Load messages from Supabase for the signed-in user and merge into
 * IndexedDB. Call after migrateLocalMessagesToAuthUser so a fresh device
 * ends up with the union of what's local and what's already synced,
 * rather than the pull clobbering anything just pushed up.
 */
export async function hydrateMessagesFromSupabase(userId: string): Promise<void> {
  const messages = await fetchMessagesForUser(userId);
  hydrating = true;
  try {
    for (const message of messages) {
      await db.chatMessages.put(message);
    }
  } finally {
    hydrating = false;
  }
}

/**
 * Push local messages belonging to this user's (now-migrated) profiles up
 * to Supabase. Unlike profiles, ChatMessage has no ownerId to reassign —
 * this only ever pushes, it never needs to rewrite the local rows.
 */
export async function migrateLocalMessagesToAuthUser(authUserId: string): Promise<void> {
  try {
    const ownedProfiles = await db.aiProfiles.where("ownerId").equals(authUserId).toArray();
    const ownedProfileIds = new Set(ownedProfiles.map((p) => p.id));
    if (ownedProfileIds.size === 0) return;

    const allMessages = await db.chatMessages.toArray();
    const toSync = allMessages.filter((m) => m.profileId && ownedProfileIds.has(m.profileId) && m.conversationId);
    for (const m of toSync) {
      await upsertMessageToSupabase(m, authUserId);
    }
  } catch (e) {
    console.warn("[syncMessages] migrate local messages error:", e);
  }
}

/**
 * Register Dexie hooks so that when a message is added or removed locally,
 * we mirror that to Supabase when the user is signed in. Only "creating"
 * and "deleting" — nothing in the app ever calls chatMessages.update/put
 * on an existing row (messages are append-only; edits create a new
 * sibling message instead, see editMessage() in ChatInterface.tsx), except
 * multiChatHistory.ts's hard-delete path, which this "deleting" hook also
 * covers automatically.
 */
export function registerSupabaseMessageHooks(dbInstance: DbWithChatMessages): void {
  if (typeof window === "undefined") return;

  dbInstance.chatMessages.hook("creating", (_primKey, message: ChatMessage) => {
    if (!hydrating && getCachedAuthUserId()) {
      upsertMessageToSupabase(message).catch(() => {});
    }
  });

  dbInstance.chatMessages.hook("deleting", (_primKey, message: ChatMessage) => {
    if (getCachedAuthUserId()) {
      deleteMessageFromSupabase(message.id).catch(() => {});
    }
  });
}
